import { useMemo, useCallback, useEffect, useState } from "react";
import type {
  TranslateFunction,
  FormatDateFunction,
  DateFormatOptions,
} from "../../contexts/localizationContext";
import { Language } from "../../constants/enums";
import { getTranslation } from "../../i18n/locales";
import { useUser } from "../../hooks";
import { getBrowserLanguage, getIntlLocale } from "../../utils/browserLanguage";
import { replacePlaceholders } from "../../utils/replacePlaceholders";
import { 
  loadLanguageFromCache, 
  saveLanguageToCache 
} from "../../utils/userPreferencesCache";
import { updateUserLanguage } from "../../apiClients/user/userClient";

/**
 * Valid Language enum values for runtime checks
 */
const VALID_LANGUAGE_VALUES = Object.values(Language).filter(
  (v) => typeof v === "number",
) as Language[];

/**
 * Type guard to check if value is a valid Language enum
 */
function isLanguageEnum(value: unknown): value is Language {
  return (
    typeof value === "number" &&
    VALID_LANGUAGE_VALUES.includes(value as Language)
  );
}

/**
 * Core translation logic
 */
function translateCore(
  key: string | undefined | null,
  targetLocale: Language,
  params: Array<string | number>,
): string {
  // Handle undefined/null keys
  if (key === undefined || key === null) {
    if (import.meta.env.DEV) {
      console.error(
        `[i18n] Translation key is undefined or null. This usually means an enum value is not mapped to a translation key.`,
      );
    }
    return String(key); // Return "undefined" or "null" as fallback
  }

  let translation = getTranslation(key, targetLocale);

  if (translation === key && targetLocale !== Language.EN) {
    if (import.meta.env.DEV) {
      console.warn(
        `[i18n] Missing translation key "${key}" for locale ${Language[targetLocale]}. Falling back to English.`,
      );
    }
    translation = getTranslation(key, Language.EN);
  }

  if (translation === key) {
    if (import.meta.env.DEV) {
      console.error(
        `[i18n] Missing translation key "${key}" in all locales (including English fallback).`,
      );
    }
    return key;
  }

  return params.length > 0
    ? replacePlaceholders(translation, params)
    : translation;
}

/**
 * Хук для управления состоянием локализации
 */
export const useLocalizationState = () => {
  const { user, updateUser, isLoggedIn } = useUser();
  
  // Инициализируем локальное состояние из localStorage или null
  const [guestLocale, setGuestLocale] = useState<Language | null>(() => {
    // При первой загрузке проверяем localStorage
    const cachedLanguage = loadLanguageFromCache();
    return cachedLanguage;
  });

  const locale = useMemo(() => {
    // Приоритет 1: Язык авторизованного пользователя с сервера
    if (
      isLoggedIn &&
      user?.languageId !== undefined &&
      user.languageId !== Language.UNSPECIFIED
    ) {
      return user.languageId;
    }
    
    // Приоритет 2: Язык из localStorage (для гостей или до загрузки профиля)
    if (guestLocale !== null && guestLocale !== Language.UNSPECIFIED) {
      return guestLocale;
    }
    
    // Приоритет 3: Язык браузера
    return getBrowserLanguage();
  }, [user?.languageId, isLoggedIn, guestLocale]);

  const t = useCallback(
    ((
      key: string | undefined | null,
      localeOrFirstParam?: Language | string | number,
      ...restParams: Array<string | number>
    ): string => {
      if (localeOrFirstParam === undefined) {
        return translateCore(key, locale, []);
      }

      if (isLanguageEnum(localeOrFirstParam)) {
        return translateCore(key, localeOrFirstParam, restParams);
      }

      return translateCore(key, locale, [localeOrFirstParam, ...restParams]);
    }) as TranslateFunction,
    [locale],
  );

  const setLocale = useCallback(
    (newLocale: Language) => {
      console.log(
        `Changing locale to ${Language[newLocale]}`,
      );

      // Всегда сохраняем в localStorage
      saveLanguageToCache(newLocale);
      
      if (isLoggedIn) {
        updateUser({ languageId: newLocale });
        // Отправляем запрос на сервер (пока-заглушка)
        updateUserLanguage(newLocale).catch((error) => {
          console.error("Failed to update user language on server:", error);
        });
      } else {
        // Для гостевых пользователей обновляем локальное состояние
        setGuestLocale(newLocale);
        console.log(
          "Guest user language saved to localStorage",
        );
      }
    },
    [isLoggedIn, updateUser],
  );

  const formatDate = useCallback(
    ((date: string | Date, options?: DateFormatOptions): string => {
      const dateObj = typeof date === "string" ? new Date(date) : date;

      if (Number.isNaN(dateObj.getTime())) {
        return typeof date === "string" ? date : "";
      }

      const {
        year = true,
        month = true,
        day = true,
        time = false,
      } = options ?? {};

      const formatOptions: Intl.DateTimeFormatOptions = {};

      if (year) formatOptions.year = "numeric";
      if (month) formatOptions.month = "2-digit";
      if (day) formatOptions.day = "2-digit";
      if (time) {
        formatOptions.hour = "2-digit";
        formatOptions.minute = "2-digit";
      }

      return new Intl.DateTimeFormat(
        getIntlLocale(locale),
        formatOptions,
      ).format(dateObj);
    }) as FormatDateFunction,
    [locale],
  );

  useEffect(() => {
    const source = isLoggedIn ? "user profile" : "localStorage/browser";
    console.log(`Current locale: ${Language[locale]} (from ${source})`);
  }, [locale, isLoggedIn]);

  // Синхронизация языка с сервера в localStorage при авторизации
  useEffect(() => {
    if (
      isLoggedIn &&
      user?.languageId !== undefined &&
      user.languageId !== Language.UNSPECIFIED
    ) {
      // Когда пользователь авторизуется, сохраняем его язык с сервера в localStorage
      const cachedLanguage = loadLanguageFromCache();
      if (cachedLanguage !== user.languageId) {
        saveLanguageToCache(user.languageId);
        console.log(
          `Synced language from server (${Language[user.languageId]}) to localStorage`
        );
      }
    }
  }, [user?.languageId, isLoggedIn]);

  return useMemo(
    () => ({
      locale,
      t,
      setLocale,
      formatDate,
    }),
    [locale, t, setLocale, formatDate],
  );
};
